import type { SemanticElement } from "schemas/generated/semanticElement";

import { useEffect, useRef } from "react";

import { useSessionUuid } from "@agent-ui/features/chat/hooks/useSessionUuid";
import { useMutation } from "@tanstack/react-query";
import { useReploAgent } from "@v2/contexts/ReploAgentContext";
import { useSearchParams } from "react-router-dom";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import {
  boundingBoxSchema,
  computedElementStylesSchema,
  semanticElementSchema,
} from "schemas/semanticElement";
import { z } from "zod";

import {
  useDisplayMode,
  useModifiersContext,
  useModifiersStoreActions,
  useSemanticElement,
} from "../store/ModifiersStoreContext";

// NOTE (Kevin, 2025-09-27): Keep in sync with
// apps/cloudflare-nextjs-scaffold/editor-scripts/scripts/notify-parent-pointerdown.ts
const CANVAS_POINTER_DOWN_MESSAGE = "replo-editor:canvas-pointerdown";
const CANVAS_KEYDOWN_MESSAGE = "replo-editor:canvas-keydown";

const globalMessageTypes = [
  CANVAS_POINTER_DOWN_MESSAGE,
  CANVAS_KEYDOWN_MESSAGE,
  "IFRAME_READY",
  "FAST_REFRESH_COMPLETE",
] as const;

type GlobalMessageType = (typeof globalMessageTypes)[number];

function isGlobalMessage(type: string): type is GlobalMessageType {
  return (globalMessageTypes as readonly string[]).includes(type);
}

const selectModeMessageTypes = [
  "ELEMENT_SELECTED",
  "HOVER_ELEMENT",
  "SELECTED_ELEMENT_SCROLL_UPDATE",
  "GROUPED_ELEMENTS_SCROLL_UPDATE",
  "TEXT_CONTENT_CHANGED",
] as const;

type SelectModeMessageType = (typeof selectModeMessageTypes)[number];

function isSelectModeMessage(type: string): type is SelectModeMessageType {
  return (selectModeMessageTypes as readonly string[]).includes(type);
}

function parseElementSelected(payload: unknown) {
  const parsed = z
    .object({ element: semanticElementSchema })
    .parse(payload) as { element: SemanticElement };
  return { element: parsed.element };
}

const textContentChangedPayloadSchema = z.object({
  selector: z.string(),
  originalText: z.string(),
  newText: z.string(),
});

const cssRectSchema = z.object({
  top: z.number(),
  left: z.number(),
  width: z.number(),
  height: z.number(),
});

const hoverElementPayloadSchema = z.object({
  rect: cssRectSchema.nullish(),
});

const scrollUpdatePayloadSchema = z.object({
  rect: cssRectSchema.optional(),
  computedStyles: computedElementStylesSchema.optional(),
  boundingBox: boundingBoxSchema.optional(),
  openingTag: z.string().optional(),
});

const groupedScrollUpdatePayloadSchema = z.object({
  groupedRects: z.array(cssRectSchema).optional(),
});

interface IframeMessageRouterDeps {
  showControls: boolean;
  hoverRef: React.RefObject<HTMLDivElement>;
  latestRectRef: React.MutableRefObject<{
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>;
  groupedElementRectsRef?: React.MutableRefObject<
    { top: number; left: number; width: number; height: number }[]
  >;
}

/**
 * Centralized router for all iframe-to-parent window messages.
 * This consolidates all message handling in one place to avoid scattered side effects.
 */
export function useIframeMessageRouter({
  showControls,
  hoverRef,
  latestRectRef,
  groupedElementRectsRef,
}: IframeMessageRouterDeps) {
  const displayMode = useDisplayMode();
  const modifierActions = useModifiersStoreActions();
  const [searchParams] = useSearchParams();
  const pageRoute = searchParams.get("pageRoute") ?? "/";
  const sessionUuid = useSessionUuid() ?? undefined;

  const { store: modifiersStore } = useModifiersContext();
  const semanticElement = useSemanticElement();
  const { actions } = useReploAgent();

  const textEditMutation = useMutation({
    mutationFn: (params: {
      elements: Parameters<typeof actions.queueSemanticFileEdit>[0]["elements"];
      instruction: string;
      pageRoute: string;
      parentSessionUuid: string;
    }) => actions.queueSemanticFileEdit(params),
  });
  const textEditMutationRef = useRef(textEditMutation);
  textEditMutationRef.current = textEditMutation;

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      const type = event.data?.type;
      if (typeof type !== "string") {
        return;
      }

      if (isGlobalMessage(type)) {
        exhaustiveSwitch({ type })({
          // NOTE (Kevin, 2025-09-27): Dispatch synthetic pointerdown so Radix
          // DismissableLayer observes it and dismisses popovers.
          [CANVAS_POINTER_DOWN_MESSAGE]: () => {
            const pointerType =
              typeof event.data.pointerType === "string"
                ? event.data.pointerType
                : "mouse";

            const syntheticEvent =
              typeof PointerEvent === "function"
                ? new PointerEvent("pointerdown", {
                    bubbles: true,
                    cancelable: true,
                    pointerType,
                  })
                : new MouseEvent("mousedown", {
                    bubbles: true,
                    cancelable: true,
                  });

            document.body.dispatchEvent(syntheticEvent);
          },

          // NOTE (Kevin, 2025-09-29): Dispatch synthetic keyboard event to propagate
          // keyboard shortcuts from iframe to parent.
          [CANVAS_KEYDOWN_MESSAGE]: () => {
            const syntheticKeyEvent = new KeyboardEvent("keydown", {
              bubbles: true,
              cancelable: true,
              key: event.data.key,
              code: event.data.code,
              metaKey: Boolean(event.data.metaKey),
              ctrlKey: Boolean(event.data.ctrlKey),
              shiftKey: Boolean(event.data.shiftKey),
              altKey: Boolean(event.data.altKey),
              repeat: Boolean(event.data.repeat),
            });

            document.body.dispatchEvent(syntheticKeyEvent);
          },

          // NOTE (Nick, 2025-12-04): Handle iframe ready handshake — clear stale selection
          // and rects from the previous iframe session, then mark the iframe as loaded.
          IFRAME_READY: () => {
            modifierActions.resetStateForIframeLoad();
            latestRectRef.current = null;
            if (groupedElementRectsRef) {
              groupedElementRectsRef.current = [];
            }
            // NOTE (Derek, 2026-02-10): Send display mode directly to the iframe so it's
            // guaranteed to arrive when contentWindow is available, preventing the race where
            // the useEffect-based SET_DISPLAY_MODE fires before the iframe is ready.
            if (event.source) {
              (event.source as Window).postMessage(
                { type: "SET_DISPLAY_MODE", payload: { displayMode } },
                "*",
              );
            }
          },

          FAST_REFRESH_COMPLETE: () => {
            // NOTE (Derek, 2026-02-10): Fast Refresh can remount EditorSelection (e.g. after
            // element deletion), resetting its displayMode state to "interactive". Re-send the
            // current displayMode immediately so hover/click handlers are restored right away.
            if (event.source) {
              (event.source as Window).postMessage(
                { type: "SET_DISPLAY_MODE", payload: { displayMode } },
                "*",
              );
            }

            // NOTE (Derek, 2026-02-09): Only clear pending previews whose source edit has been applied
            // by this HMR cycle. Previews created after the HMR event started are for edits still in
            // flight and must survive. We use a 2-second delay to allow Tailwind classes to apply first.
            // NOTE (Derek, 2026-02-19): Read from store at call time to avoid closing over
            // pendingPreviews (which would cause the listener to tear down/re-register on every
            // preview add/remove).
            const currentPreviews = modifiersStore.getState().pendingPreviews;
            if (currentPreviews.length > 0) {
              const hmrTimestamp = Date.now();
              setTimeout(() => {
                for (const preview of currentPreviews) {
                  if (preview.createdAt < hmrTimestamp) {
                    modifierActions.removePendingPreview(preview.selector);
                  }
                }
              }, 2000);
            }
          },
        });
        return;
      }

      if (!showControls || displayMode !== "select") {
        return;
      }

      if (isSelectModeMessage(type)) {
        exhaustiveSwitch({ type })({
          ELEMENT_SELECTED: () => {
            const { element } = parseElementSelected(event.data.payload);

            // NOTE (Yuxin, 2026-02-03): setSemanticElement already clears fiber selection
            modifierActions.setSemanticElement(element);

            latestRectRef.current = {
              top: element.boundingBox.y,
              left: element.boundingBox.x,
              width: element.boundingBox.width,
              height: element.boundingBox.height,
            };
          },

          HOVER_ELEMENT: () => {
            if (!hoverRef.current) {
              return;
            }
            const { rect } = hoverElementPayloadSchema.parse(
              event.data.payload,
            );

            if (rect) {
              Object.assign(hoverRef.current.style, {
                top: `${rect.top}px`,
                left: `${rect.left}px`,
                width: `${rect.width}px`,
                height: `${rect.height}px`,
                display: "block",
              });
            } else {
              hoverRef.current.style.display = "none";
            }
          },

          SELECTED_ELEMENT_SCROLL_UPDATE: () => {
            const { rect, computedStyles, boundingBox, openingTag } =
              scrollUpdatePayloadSchema.parse(event.data.payload);
            if (rect) {
              latestRectRef.current = rect;
              // NOTE (Derek, 2025-01-14, REPL-20840): Store in modifiers store so dimension selectors
              // can show computed pixel values when in Hug/Fill mode
              modifierActions.setComputedElementDimensions(rect);
            }
            // NOTE (Derek, 2026-02-11): After APPLY_PREVIEW, the iframe sends updated computed
            // styles and bounding box so the modifier panel and overlay stay in sync.
            if (computedStyles && boundingBox) {
              modifierActions.updateSemanticElementAfterPreview({
                boundingBox,
                computedStyles,
                openingTag,
              });
            }
          },

          // NOTE (Nick, 2025-12-02): Handle continuous updates to grouped element rects
          GROUPED_ELEMENTS_SCROLL_UPDATE: () => {
            const { groupedRects } = groupedScrollUpdatePayloadSchema.parse(
              event.data.payload,
            );
            if (groupedElementRectsRef && groupedRects) {
              groupedElementRectsRef.current = groupedRects;
            }
          },

          // NOTE (Yuxin, 2026-02-03): Persist text content changes from in-place editor
          // via the agent's queueSemanticFileEdit RPC.
          TEXT_CONTENT_CHANGED: () => {
            const { selector, originalText, newText } =
              textContentChangedPayloadSchema.parse(event.data.payload);

            if (newText.trim() === originalText.trim() || !newText.trim()) {
              return;
            }

            if (textEditMutationRef.current.isPending || !semanticElement) {
              return;
            }

            if (!sessionUuid) {
              return;
            }

            textEditMutationRef.current.mutate({
              elements: [
                {
                  elementType: semanticElement.elementType,
                  selector,
                  text: newText,
                  cssClasses: semanticElement.cssClasses,
                  sourceLocation: semanticElement.sourceLocation,
                },
              ],
              instruction: `Change the text content to: "${newText}"`,
              pageRoute,
              parentSessionUuid: sessionUuid,
            });
          },
        });
      }
    };

    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [
    displayMode,
    showControls,
    modifierActions,
    hoverRef,
    latestRectRef,
    groupedElementRectsRef,
    pageRoute,
    sessionUuid,
    semanticElement,
    modifiersStore,
  ]);
}
